/** @format */

import { useEffect, useRef } from 'react';

type InformationWidgetData = {
  w?: number;
  h?: number;
  title?: string;
  content?: string;
};

type InformationWidgetProps = {
  data?: InformationWidgetData;
  onSizeChange?: (height: number) => void;
  currentBreakPoint?: string | null;
  globalLayouts?: Record<string, unknown>;
};

const InformationWidget = (props: InformationWidgetProps) => {
  const { data = {}, onSizeChange, currentBreakPoint } = props;
  const measureRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const lastHeightRef = useRef(0);
  const rafIdRef = useRef(0);

  useEffect(() => {
    if (!innerRef.current || !onSizeChange || !currentBreakPoint) return undefined;

    const reportSize = () => {
      rafIdRef.current = 0;
      if (!innerRef.current) return;

      const height = innerRef.current.offsetHeight;
      if (Math.abs(height - lastHeightRef.current) < 1) return;

      lastHeightRef.current = height;
      onSizeChange(height);
    };

    const scheduleReport = () => {
      if (rafIdRef.current) return;
      rafIdRef.current = requestAnimationFrame(reportSize);
    };

    scheduleReport();

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleReport) : null;
    const target = measureRef.current;

    if (observer && target) observer.observe(target);

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      observer?.disconnect();
    };
  }, [currentBreakPoint, onSizeChange, data.content]);

  return (
    <div className="information-widget" data-width={data.w} data-height={data.h} ref={measureRef}>
      <div className="information-widget-inner" ref={innerRef}>
        <h2 className="information-widget-title">{data.title}</h2>
        <div
          className="information-widget-content content-block"
          dangerouslySetInnerHTML={{ __html: data.content || '' }}
        ></div>
      </div>
    </div>
  );
};

export { InformationWidget };
